/**
 * Inline recommendation cards rendered as a Preact island inside the
 * assistant message bubble. First leaf migrated as part of the Preact
 * rewrite (branch `widget/preact-rewrite`). Mounted by `ui.ts` via
 * `mountPreactIsland` — the imperative shell still owns the bubble
 * wrapper + scroll behavior; this component owns the card markup +
 * click/keyboard interactions.
 *
 * Behavior parity targets (don't drift):
 * - vimeo CDN thumbnails occasionally 404 → img.onerror swaps to icon
 * - missing image → type-icon fallback (PDF/video/image/tour glyphs)
 * - keyboard activation: Enter + Space (legacy parity)
 * - label fallback chain: extension → raw extension → content_type → 'Content'
 */

import { useState } from 'preact/hooks';
import type { PlaylistItem } from './playlist-drawer';
import { ThumbnailIcon, typeLabelFor } from './content-thumbnail';

function Card({
  item,
  onSelect,
}: {
  item: PlaylistItem;
  onSelect: (it: PlaylistItem) => void;
}) {
  // Local state so an img.onerror fallback toggles the icon without
  // pushing state up. Card lifetime is the message bubble's lifetime;
  // no need to memoize across recommendation batches.
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = !!item.image && !imageFailed;

  const activate = (): void => onSelect(item);

  return (
    <div
      class="sp-rec-card"
      role="button"
      tabIndex={0}
      onClick={activate}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          activate();
        }
      }}
    >
      <div class="sp-rec-image">
        {showImage ? (
          <img
            src={item.image ?? ''}
            alt={item.name}
            onError={() => setImageFailed(true)}
          />
        ) : (
          <ThumbnailIcon ext={item.content_extension} />
        )}
      </div>
      <div class="sp-rec-info">
        <div class="sp-rec-name">{item.name}</div>
        <div class="sp-rec-type">
          {typeLabelFor(item.content_extension, item.content_type)}
        </div>
      </div>
      <div class="sp-rec-action">
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M9 18l6-6-6-6" />
        </svg>
      </div>
    </div>
  );
}

export function RecommendationCards({
  items,
  onSelect,
}: {
  items: PlaylistItem[];
  onSelect: (it: PlaylistItem) => void;
}) {
  if (items.length === 0) return null;
  return (
    <div class="sp-rec-cards">
      {items.map((it) => (
        <Card key={`${it.content_id}-${it.content_type}`} item={it} onSelect={onSelect} />
      ))}
    </div>
  );
}
